import React from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import ImageGallery from "react-image-gallery";

import GalleryNavBar from "./GalleryNavBar";
import { CloseGalleryButton } from "./CloseGalleryButton";
import { GalleryAnswers } from "./GalleryAnswers";

import { renderGalleryItem, renderGalleryThumbnail, renderLeftNav, renderRightNav } from "./utils";
import { isNarrowScreen } from "../../lib/constants";
import { onGallerySlide } from "./actions";
import { addMobileOrLandscapeClass, getIsMobileLandscape, Show } from "../../lib/utils";
import { getGalleryContent, getGalleryItems } from "./selectors";
import type { RichModule } from "../../lib/types";

type Props = {
  isMobileLandscape: boolean,
  galleryModule: RichModule,
};

/**
 * Render gallery content in fullscreen mode and in in-chat mode.
 */
export const GalleryInner = ({ isMobileLandscape = false, galleryModule }: Props) => {
  const dispatch = useDispatch();
  const galleryItems = useSelector((state) => getGalleryItems(state, galleryModule));
  const galleryContent = useSelector((state) => getGalleryContent(state, galleryModule));

  const showNav = !(galleryModule && (galleryModule.isDigitalTour() || galleryModule.isVideo()));
  const showAnswers = !galleryModule.isInChatGallery() && !galleryModule.isInChatDigitalTour();

  return (
    <>
      <div className={addMobileOrLandscapeClass("gallery column")}>
        <Show when={!getIsMobileLandscape()}>
          <GalleryNavBar />
        </Show>
        <GalleryStylesWrapper>
          <ImageGallery
            items={galleryItems}
            showPlayButton={false}
            showBullets={showNav && !isMobileLandscape}
            showNav={!isNarrowScreen}
            showThumbnails={!isMobileLandscape && galleryContent.length > 1}
            showFullscreenButton={false}
            onSlide={(index) => dispatch(onGallerySlide(index))}
            renderLeftNav={renderLeftNav}
            renderRightNav={renderRightNav}
            renderItem={renderGalleryItem}
            renderThumbInner={renderGalleryThumbnail}
          />
        </GalleryStylesWrapper>
      </div>

      <div className={addMobileOrLandscapeClass("right-column column")}>
        <Show when={showAnswers}>
          <GalleryAnswers />
          <CloseGalleryButton />
        </Show>
      </div>
    </>
  );
};

export const GalleryStylesWrapper = styled.div`
  .image-gallery-thumbnail {
    cursor: pointer;
    border-radius: 9px;
    vertical-align: middle;
    border: 2px solid transparent;
    transition: all 0.3s ease-in-out;

    &.active {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
    }

    &:hover,
    &:focus,
    &.active {
      border: 2px solid ${(props) => props.theme.brandColor || "#337ab7"};
    }

    img {
      border-radius: 6px;
    }
  }
`;

/**
 * Styling wrapper for inchat gallery and inchat floorplans.
 */
export const InChatGalleryStylesWrapper = styled.div`
  .nav-arrow {
    position: absolute;
    right: 20px;
    filter: drop-shadow(0 0 2px rgba(255, 255, 255, 1));

    &.left {
      left: 20px;
    }
  }

  .image-gallery-thumbnails .image-gallery-thumbnail {
    max-height: 36px;
    max-width: 60px;
    overflow: hidden;

    &.active {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
    }
  }
`;
